import * as coreClient from "@azure/core-client";
import * as coreRestPipeline from "@azure/core-rest-pipeline";

export type DevBoxOperationUnion =
  | DevBoxOperation
  | DevBoxStartOperation
  | DevBoxStopOperation
  | DevBoxRestartOperation
  | DevBoxRepairOperation;
export type EnvironmentOperationUnion =
  | EnvironmentOperation
  | EnvironmentDeployOperation
  | EnvironmentDeleteOperation;

/** Results of the project list operation. */
export interface ProjectListResult {
  /** Current page of results. */
  value: Project[];
  /** URL to get the next set of results if there are any. */
  nextLink?: string;
}

/** Project details. */
export interface Project {
  /** The unique URI of the project */
  uri: string;
  /** Name of the project */
  name: string;
  /** Description of the project. */
  description?: string;
  /** When specified, indicates the maximum number of Dev Boxes a single user can create across all pools in the project. */
  maxDevBoxesPerUser?: number;
  /** Display name of the project. */
  displayName?: string;
}

/** An error response from the service. */
export interface CloudError {
  /** Error body */
  error: CloudErrorBody;
}

/** An error response from the service. */
export interface CloudErrorBody {
  /** An identifier for the error. Codes are invariant and are intended to be consumed programmatically. */
  code: string;
  /** A message describing the error, intended to be suitable for display in a user interface. */
  message: string;
  /** The target of the particular error. For example, the name of the property in error. */
  target?: string;
  /** A list of additional details about the error. */
  details?: CloudErrorBody[];
}

/** Abilities a user has on a project. */
export interface ProjectAbilities {
  /** The abilities the user has to perform actions on the project as an admin. */
  abilitiesAsAdmin: ProjectAbilityAsAdmin[];
  /** The abilities the user has to perform actions on the project as a developer. */
  abilitiesAsDeveloper: ProjectAbilityAsDeveloper[];
}

/** The Pool list result */
export interface PoolListResult {
  /** Current page of results */
  value: Pool[];
  /** The URL to get the next set of results. */
  nextLink?: string;
}

/** A pool of Dev Boxes. */
export interface Pool {
  /** The unique URI of the pool */
  uri: string;
  /** Pool name */
  name: string;
  /** Azure region where Dev Boxes in the pool are located */
  location: string;
  /** The operating system type of Dev Boxes in this pool */
  osType?: "Windows";
  /** Hardware settings for the Dev Boxes created in this pool */
  hardwareProfile?: HardwareProfile;
  /** Indicates whether hibernate is enabled/disabled or unknown. */
  hibernateSupport?: HibernateSupport;
  /** Storage settings for Dev Box created in this pool */
  storageProfile?: StorageProfile;
  /** Image settings for Dev Boxes create in this pool */
  imageReference?: ImageReference;
  /** Indicates whether owners of Dev Boxes in this pool are local administrators on the Dev Boxes. */
  localAdministrator?: LocalAdminStatus;
  /** Stop on disconnect configuration settings for Dev Boxes created in this pool. */
  stopOnDisconnect?: StopOnDisconnectConfiguration;
  /** Overall health status of the Pool. Indicates whether or not the Pool is available to create Dev Boxes. */
  healthStatus: PoolHealthStatus;
  /** Display name of the pool. */
  displayName?: string;
}

/** Hardware specifications for the Dev Box. */
export interface HardwareProfile {
  /**
   * The name of the SKU
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly skuName?: string;
  /**
   * The number of vCPUs available for the Dev Box.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly vCPUs?: number;
  /**
   * The amount of memory available for the Dev Box.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly memoryGB?: number;
}

/** Storage settings for the Dev Box's disks */
export interface StorageProfile {
  /** Settings for the operating system disk. */
  osDisk?: OSDisk;
}

/** Settings for the operating system disk. */
export interface OSDisk {
  /**
   * The size of the OS Disk in gigabytes.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly diskSizeGB?: number;
}

/** Specifies information about the image used */
export interface ImageReference {
  /**
   * The name of the image used.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly name?: string;
  /**
   * The version of the image.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly version?: string;
  /**
   * The operating system of the image.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly operatingSystem?: string;
  /**
   * The operating system build number of the image.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly osBuildNumber?: string;
  /**
   * The datetime that the backing image version was published.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly publishedDate?: Date;
}

/** Stop on disconnect configuration settings for Dev Boxes created in this pool. */
export interface StopOnDisconnectConfiguration {
  /** Indicates whether the feature to stop the devbox on disconnect once the grace period has lapsed is enabled. */
  status: StopOnDisconnectEnableStatus;
  /** The specified time in minutes to wait before stopping a Dev Box once disconnect is detected. */
  gracePeriodMinutes?: number;
}

/** The Schedule list result */
export interface ScheduleListResult {
  /** Current page of results */
  value: Schedule[];
  /** The URL to get the next set of results. */
  nextLink?: string;
}

/** A Schedule to execute action. */
export interface Schedule {
  /** The unique URI of the schedule */
  uri: string;
  /** Display name for the Schedule */
  name: string;
  /** The URI of the resource that this schedule belongs to */
  sourceUri: string;
  /** The type of the resource that this schedule belongs to */
  sourceType: "Pool";
  /** Supported type this scheduled task represents. */
  type: "StopDevBox";
  /** The frequency of this scheduled task. */
  frequency: "Daily";
  /** The target time to trigger the action. The format is HH:MM. */
  time: string;
  /** The IANA timezone id at which the schedule should execute. */
  timeZone: string;
}

/** Result of listing customization tasks. */
export interface CustomizationTaskDefinitionListResult {
  /** Current page of results. */
  value: CustomizationTaskDefinition[];
  /** URL to get the next set of results if there are any. */
  nextLink?: string;
}

/** Represents a task to be used in customizing a Dev Box. */
export interface CustomizationTaskDefinition {
  /** Full name of the task: {catalogName}/{taskName}. */
  name: string;
  /** Name of the catalog that the task belongs to. */
  catalogName: string;
  /**
   * The unique URI of the customization task.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly uri?: string;
  /** Description of the task. */
  description?: string;
  /** Parameters for the task. */
  parameters?: { [propertyName: string]: CustomizationTaskDefinitionParameter };
}

/** Parameters for a customization task. */
export interface CustomizationTaskDefinitionParameter {
  /** Description of the parameter. */
  description?: string;
  /** Type of the parameter. */
  type: CustomizationTaskDefinitionParameterType;
  /** Whether or not the parameter is required. */
  required?: boolean;
  /** Default value for the parameter. */
  default?: string;
  /** Allowed values for the parameter. */
  allowed?: string[];
}

/** Represents a list of tasks to apply to a Dev Box */
export interface CustomizationTaskList {
  /** Tasks to apply. */
  tasks?: CustomizationTask[];
}

/** A customization task to run on a Dev Box. */
export interface CustomizationTask {
  /** Name of the task. */
  name: string;
  /** Parameters for the task. */
  parameters?: { [propertyName: string]: string };
  /** Display name to help differentiate multiple instances of the same task. */
  displayName?: string;
  /** Timeout, in seconds. Overrides any timeout provided on the task definition. */
  timeout?: number;
  /** What account to run the task as. */
  runAs?: CustomizationTaskExecutionAccount;
  /**
   * ID of the task instance.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly id?: string;
  /**
   * The unique URI for retrieving the task logs.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly logUri?: string;
  /**
   * Status of the task.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly status?: CustomizationTaskStatus;
  /**
   * Start time of the task.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly startTime?: Date;
  /**
   * End time of the task.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly endTime?: Date;
}

/** The result of validating a list of customization tasks. */
export interface CustomizationTaskListValidationResult {
  /** Outcome of validation. */
  validationResult: CustomizationTaskListValidationStatus;
  /** List of validation errors. Absent if no errors. */
  errors?: CustomizationTaskListValidationError[];
}

/** All of the validation errors for a customization task. */
export interface CustomizationTaskListValidationError {
  /** The customization task that the error is about. */
  target: CustomizationTask;
  /** List of validation errors for the task. */
  details: CustomizationTaskListValidationErrorDetails[];
}

/** A single error from validating a task. */
export interface CustomizationTaskListValidationErrorDetails {
  /** An identifier for the error. Codes are invariant and are intended to be consumed programmatically. */
  code: string;
  /** A message describing the error, intended to be suitable for display in a user interface. */
  message: string;
}

/** The current status of an async operation */
export interface OperationStatus {
  /** Fully qualified ID for the operation status. */
  id?: string;
  /** The operation id name */
  name?: string;
  /** Provisioning state of the resource. */
  status: string;
  /** The id of the resource. */
  resourceId?: string;
  /** The start time of the operation */
  startTime?: Date;
  /** The end time of the operation */
  endTime?: Date;
  /** Percent of the operation that is complete */
  percentComplete?: number;
  /** Custom operation properties, populated only for a successful operation. */
  properties?: Record<string, unknown>;
  /** Operation Error message */
  error?: OperationStatusError;
}

/** Operation Error message */
export interface OperationStatusError {
  /** The error code. */
  code?: string;
  /** The error message. */
  message?: string;
}

/** The Dev Box list result */
export interface DevBoxListResult {
  /** The list of DevBox Dev Boxes */
  value: DevBox[];
  /** The URL to get the next set of results. */
  nextLink?: string;
}

/** A Dev Box */
export interface DevBox {
  /**
   * The unique URI of the dev box
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly uri?: string;
  /**
   * Display name for the Dev Box
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly name?: string;
  /**
   * Name of the project this Dev Box belongs to
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly projectName?: string;
  /** The name of the Dev Box pool this machine belongs to. */
  poolName: string;
  /**
   * Indicates whether hibernate is enabled/disabled or unknown.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly hibernateSupport?: HibernateSupport;
  /**
   * The current provisioning state of the Dev Box.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly provisioningState?: string;
  /**
   * The current action state of the Dev Box. This is state is based on previous action performed by user.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly actionState?: string;
  /**
   * The current power state of the Dev Box.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly powerState?: PowerState;
  /**
   * A unique identifier for the Dev Box. This is a GUID-formatted string (e.g. 00000000-0000-0000-0000-000000000000).
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly uniqueId?: string;
  /**
   * Provisioning or action error details. Populated only for error states.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly error?: CloudErrorBody;
  /**
   * Azure region where this Dev Box is located. This will be the same region as the Virtual Network it is attached to.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly location?: string;
  /**
   * The operating system type of this Dev Box.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly osType?: "Windows";
  /**
   * The AAD object id of the user this Dev Box is assigned to.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly user?: string;
  /**
   * Information about the Dev Box's hardware resources
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly hardwareProfile?: HardwareProfile;
  /**
   * Storage settings for this Dev Box
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly storageProfile?: StorageProfile;
  /**
   * Information about the image used for this Dev Box
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly imageReference?: ImageReference;
  /**
   * Creation time of this Dev Box
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly createdTime?: Date;
  /** Indicates whether the owner of the Dev Box is a local administrator. */
  localAdministrator?: LocalAdminStatus;
}

/** List of customization groups applied to a Dev Box. */
export interface CustomizationGroupListResult {
  /** The list of customization groups. */
  value: CustomizationGroup[];
  /** The URL to get the next set of results. */
  nextLink?: string;
}

/** Represents a list of tasks to apply to a Dev Box */
export interface CustomizationGroup {
  /** Tasks to apply. Note by default tasks are excluded from the response when listing customization groups. To include them, use the `include=tasks` query parameter. */
  tasks?: CustomizationTask[];
  /**
   * The unique URI of the customization group.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly uri?: string;
  /**
   * Name of the customization group.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly name?: string;
  /**
   * Status of the customization group.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly status?: CustomizationGroupStatus;
  /**
   * Start time of the customization group.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly startTime?: Date;
  /**
   * End time of the customization group.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly endTime?: Date;
}

/** Provides remote connection information for a Dev Box. */
export interface RemoteConnection {
  /** URL to open a browser based RDP session. */
  webUrl?: string;
  /** Link to open a Remote Desktop session. */
  rdpConnectionUrl?: string;
  /** Link to open a remote desktop session via a dev box's underlying Cloud PC. (This will default to Windows App) */
  cloudPcConnectionUrl?: string;
}

/** The actions list result */
export interface DevBoxActionsListResult {
  /** Current page of results */
  value: DevBoxAction[];
  /** The URL to get the next set of results. */
  nextLink?: string;
}

/** An action which will take place on a Dev Box. */
export interface DevBoxAction {
  /** The unique URI for the Dev Box action. */
  uri: string;
  /** The name of the action. */
  name: string;
  /** The action that will be taken. */
  actionType: "Stop";
  /** The id of the resource which triggered this action */
  sourceId: string;
  /** The URI of the resource which triggered this action */
  sourceUri: string;
  /** The type of the resource which triggered this action */
  sourceType: DevBoxActionSourceType;
  /** The earliest time that the action could occur (UTC). */
  suspendedUntil?: Date;
  /** Details about the next run of this action. */
  next?: DevBoxNextAction;
}

/** Details about the next run of an action. */
export interface DevBoxNextAction {
  /** The time the action will be triggered (UTC). */
  scheduledTime: Date;
}

/** The actions list result */
export interface DevBoxActionsDelayMultipleResult {
  /** Current page of results */
  value: DevBoxActionDelayResult[];
  /** The URL to get the next set of results. */
  nextLink?: string;
}

/** The action delay result */
export interface DevBoxActionDelayResult {
  /** The unique URI of the action. */
  uri: string;
  /** The name of the action. */
  name: string;
  /** The result of the delay operation on this action. */
  result: DevBoxActionDelayResultStatus;
  /** The delayed action */
  action?: DevBoxAction;
  /** Information about the error that occurred. Only populated on error. */
  error?: CloudErrorBody;
}

/** The result of listing operations on a Dev Box. */
export interface DevBoxOperationListResult {
  /** The current page of results. */
  value: DevBoxOperationUnion[];
  /** The URL to get the next set of results. */
  nextLink?: string;
}

/** Information about an operation on a Dev Box. */
export interface DevBoxOperation {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "Start" | "Stop" | "Restart" | "Repair";
  /** The unique URI for the Dev Box operation. */
  uri: string;
  /** Unique identifier for the Dev Box operation. */
  operationId: string;
  /** The operation status. */
  status: DevBoxOperationStatus;
  /** The object ID of the actor which initiated the operation. */
  createdByObjectId?: string;
  /** The time the operation started. */
  startTime?: Date;
  /** The time the operation finished. */
  endTime?: Date;
  /** Provisioning or operation error details. Populated only for error states. */
  error?: CloudErrorBody;
}

/** Results of the environment list operation. */
export interface EnvironmentListResult {
  /** Current page of results. */
  value: Environment[];
  /** URL to get the next set of results if there are any. */
  nextLink?: string;
}

/** Properties of an environment. These properties can be updated via PATCH after the resource has been created. */
export interface EnvironmentPatchProperties {
  /** The time the expiration date will be triggered (UTC), after which the environment and associated resources will be deleted. */
  expirationDate?: Date;
}

/** Outputs from environment deployment. */
export interface EnvironmentOutputs {
  /** The outputs Names and Values */
  outputs?: { [propertyName: string]: EnvironmentOutput };
}

/** Output from environment deployment. */
export interface EnvironmentOutput {
  /** Type of the output value */
  type?: EnvironmentOutputType;
  /** The output value */
  value?: Record<string, unknown>;
  /** Indicates if the value is sensitive. */
  sensitive?: boolean;
}

/** The action results list result. */
export interface EnvironmentOperationListResult {
  /** The current page of results. */
  value: EnvironmentOperationUnion[];
  /** The URL to get the next set of results. */
  nextLink?: string;
}

/** Information about an operation on an environment. */
export interface EnvironmentOperation {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "Deploy" | "Delete";
  /** The unique URI for the environment operation. */
  uri: string;
  /** Unique identifier for the environment operation. */
  operationId: string;
  /** The operation status. */
  status: EnvironmentOperationStatus;
  /** The object ID of the actor which initiated the operation. */
  createdByObjectId?: string;
  /** The time the operation started. */
  startTime?: Date;
  /** The time the operation finished. */
  endTime?: Date;
  /** Parameters object for the environment at the time of the operation. */
  environmentParameters?: Record<string, unknown>;
  /** Provisioning or operation error details. Populated only for error states. */
  error?: CloudErrorBody;
}

/** The actions list result */
export interface EnvironmentActionsListResult {
  /** Current page of results */
  value: EnvironmentAction[];
  /** The URL to get the next set of results. */
  nextLink?: string;
}

/** An upcoming Environment Action. */
export interface EnvironmentAction {
  /**
   * Uniquely identifies the action.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly name?: string;
  /**
   * Uri of the action resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly uri?: string;
  /** The action that will be taken. */
  actionType: "Delete";
  /** Details about the next run of this action. */
  next: EnvironmentNextAction;
  /**
   * Object Id of the user who last modified the action.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly lastModifiedBy?: string;
  /**
   * time the object was last modified.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly lastModifiedAt?: Date;
}

/** Details about the next run of an action. */
export interface EnvironmentNextAction {
  /** The time the action will be triggered (UTC). */
  scheduledTime: Date;
}

/** Results of the catalog list operation. */
export interface CatalogListResult {
  /** Current page of results. */
  value: Catalog[];
  /** URL to get the next set of results if there are any. */
  nextLink?: string;
}

/** A catalog. */
export interface Catalog {
  /** The unique URI of the catalog */
  uri: string;
  /** Name of the catalog. */
  name: string;
}

/** Results of the environment definition list operation. */
export interface EnvironmentDefinitionListResult {
  /** Current page of results. */
  value: EnvironmentDefinition[];
  /** URL to get the next set of results if there are any. */
  nextLink?: string;
}

/** An environment definition. */
export interface EnvironmentDefinition {
  /** The unique URI of the environment definition */
  uri: string;
  /** The ID of the environment definition. */
  id: string;
  /** Name of the environment definition. */
  name: string;
  /** Name of the catalog. */
  catalogName: string;
  /** A short description of the environment definition. */
  description?: string;
  /** Input parameters passed to an environment. */
  parameters?: EnvironmentDefinitionParameter[];
  /** JSON schema defining the parameters object passed to an environment. */
  parametersSchema?: string;
  /** Path to the Environment Definition entrypoint file. */
  templatePath?: string;
}

/** Properties of an Environment Definition parameter */
export interface EnvironmentDefinitionParameter {
  /** Unique ID of the parameter */
  id: string;
  /** Display name of the parameter */
  name?: string;
  /** Description of the parameter */
  description?: string;
  /** Default value of the parameter */
  default?: string;
  /** A string of one of the basic JSON types (number, integer, array, object, boolean, string) */
  type: ParameterType;
  /** Whether or not this parameter is read-only.  If true, default should have a value. */
  readOnly?: boolean;
  /** Whether or not this parameter is required */
  required: boolean;
  /** An array of allowed values */
  allowed?: string[];
}

/** Result of the environment type list operation. */
export interface EnvironmentTypeListResult {
  /** Current page of results. */
  value: EnvironmentType[];
  /** URL to get the next set of results if there are any. */
  nextLink?: string;
}

/** Properties of an environment type. */
export interface EnvironmentType {
  /** The unique URI of the environment type */
  uri: string;
  /** Name of the environment type */
  name: string;
  /** The ID of a subscription or management group that the environment type will be mapped to. The environment's resources will be deployed into this subscription or management group. */
  deploymentTargetId: string;
  /** Indicates whether this environment type is enabled for use in this project. */
  status: EnvironmentTypeEnableStatus;
  /** Display name of the environment type. */
  displayName?: string;
}

/** Abilities a user has on an environment type. */
export interface EnvironmentTypeAbilities {
  /** The abilities the user has to perform actions on the environment type as an admin. */
  abilitiesAsAdmin: EnvironmentTypeAbilityAsAdmin[];
  /** The abilities the user has to perform actions on the environment type as a developer. */
  abilitiesAsDeveloper: EnvironmentTypeAbilityAsDeveloper[];
}

/** The NotificationSettings configured for user. */
export interface NotificationSettings {
  /** The name of the notification settings. */
  name: "default";
  /** If notification is enabled for the user. */
  enabled: boolean;
  /** The culture that MEO can accommdate requests to send emails in. */
  culture: string;
  /** The type of the notification */
  notificationType: NotificationType;
}

/** The type of the notification */
export interface NotificationType {
  /** The notification configured for DevBox provisioning. */
  devBoxProvisioningNotification: DevBoxProvisioningNotification;
}

/** The notification configured for DevBox provisioning. */
export interface DevBoxProvisioningNotification {
  /** If notification is enabled for DevBox provisioning. */
  enabled: boolean;
  /** The channel of the notification */
  notificationChannel: NotificationChannel;
}

/** The channel of the notification */
export interface NotificationChannel {
  /** The email notification */
  emailNotification: EmailNotification;
  /** The webhook notification */
  webhookNotification: WebhookNotification;
}

/** The email notification */
export interface EmailNotification {
  /** If email notification is enabled */
  enabled: boolean;
  /** The recipients of the email notification */
  recipients?: string;
  /** The cc of the email notification */
  cc?: string;
}

/** The webhook notification */
export interface WebhookNotification {
  /** If webhook notification is enabled */
  enabled: boolean;
  /** The url of the webhook */
  url?: string;
}

/** The allowed cultures list result for NotificationSettings */
export interface NotificationSettingsAllowedCulturesListResult {
  /** Allowed culture codes for NotificationSettings */
  value: string[];
}

/** Results of the artifact list operation. */
export interface ArtifactListResult {
  /** Current page of results. */
  value: Artifact[];
  /** URL to get the next set of results if there are any. */
  nextLink?: string;
}

/** Properties of an Artifact */
export interface Artifact {
  /**
   * Artifact identifier
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly id?: string;
  /**
   * Artifact name
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly name?: string;
  /**
   * Whether artifact is a directory
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly isDirectory?: boolean;
  /**
   * Uri where the file contents can be downloaded
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly downloadUri?: string;
  /**
   * Size of file in bytes, if the artifact is a file
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly fileSize?: number;
  /**
   * Time the artifact was created
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly createdTime?: Date;
  /**
   * Time the artifact was last modified
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly lastModifiedTime?: Date;
}

/** Information about the result of a repair operation on a Dev Box. */
export interface DevBoxRepairOperationResult {
  /** The outcome of the repair operation. */
  repairOutcome?: DevBoxRepairOutcome;
  /** The result code associated with the repair operation. */
  code?: string;
  /** The result message associated with the repair operation. */
  message?: string;
}

/** The operation result of validating a list of customization tasks. */
export interface CustomizationTaskListValidationOperationResult
  extends OperationStatus {
  /** Outcome of validation. */
  result?: CustomizationTaskListValidationResult;
}

/** Information about a start operation on a Dev Box. */
export interface DevBoxStartOperation extends DevBoxOperation {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "Start";
}

/** Information about a stop operation on a Dev Box. */
export interface DevBoxStopOperation extends DevBoxOperation {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "Stop";
}

/** Information about a restart operation on a Dev Box. */
export interface DevBoxRestartOperation extends DevBoxOperation {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "Restart";
}

/** Information about a repair operation on a Dev Box. */
export interface DevBoxRepairOperation extends DevBoxOperation {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "Repair";
  /** The result of the repair operation. */
  result?: DevBoxRepairOperationResult;
}

/** Properties of an environment. These properties can be updated after the resource has been created. */
export interface EnvironmentUpdateProperties
  extends EnvironmentPatchProperties {
  /** Parameters object for the environment. Can only be updated via create or replace. */
  parameters?: Record<string, unknown>;
}

/** Information about a deploy operation on an environment. */
export interface EnvironmentDeployOperation extends EnvironmentOperation {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "Deploy";
}

/** Information about a delete operation on an environment. */
export interface EnvironmentDeleteOperation extends EnvironmentOperation {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  kind: "Delete";
}

/** Properties of an environment. */
export interface Environment extends EnvironmentUpdateProperties {
  /**
   * The unique URI of the environment
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly uri?: string;
  /**
   * Environment name.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly name?: string;
  /** The name of the environment type. Cannot be updated after creation. */
  environmentType: string;
  /**
   * The AAD object id of the owner of this Environment.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly user?: string;
  /**
   * The provisioning state of the environment.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly provisioningState?: string;
  /**
   * The identifier of the resource group containing the environment's resources.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly resourceGroupId?: string;
  /** The name of the catalog. Cannot be updated after creation. */
  catalogName: string;
  /** The name of the environment definition. Cannot be updated after creation. */
  environmentDefinitionName: string;
  /**
   * Provisioning error details. Populated only for error states.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly error?: CloudErrorBody;
}

/** Defines headers for DevCenter_listAllDevBoxes operation. */
export interface DevCenterListAllDevBoxesExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevCenter_listAllDevBoxesByUser operation. */
export interface DevCenterListAllDevBoxesByUserExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevCenter_getNotificationSettings operation. */
export interface DevCenterGetNotificationSettingsExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevCenter_createNotificationSettings operation. */
export interface DevCenterCreateNotificationSettingsExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevCenter_listNotificationSettingsAllowedCultures operation. */
export interface DevCenterListNotificationSettingsAllowedCulturesExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevCenter_listAllDevBoxesNext operation. */
export interface DevCenterListAllDevBoxesNextExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevCenter_listAllDevBoxesByUserNext operation. */
export interface DevCenterListAllDevBoxesByUserNextExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_listPools operation. */
export interface DevBoxesListPoolsExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_getPool operation. */
export interface DevBoxesGetPoolExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_listSchedulesByProject operation. */
export interface DevBoxesListSchedulesByProjectExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_listSchedulesByPool operation. */
export interface DevBoxesListSchedulesByPoolExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_getScheduleByPool operation. */
export interface DevBoxesGetScheduleByPoolExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_listDevBoxesByUser operation. */
export interface DevBoxesListDevBoxesByUserExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_getDevBoxByUser operation. */
export interface DevBoxesGetDevBoxByUserExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_createDevBox operation. */
export interface DevBoxesCreateDevBoxExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_deleteDevBox operation. */
export interface DevBoxesDeleteDevBoxHeaders {
  /** URL to query for status of the operation. */
  operationLocation?: string;
}

/** Defines headers for DevBoxes_deleteDevBox operation. */
export interface DevBoxesDeleteDevBoxExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_startDevBox operation. */
export interface DevBoxesStartDevBoxHeaders {
  /** URL to query for status of the operation. */
  operationLocation?: string;
}

/** Defines headers for DevBoxes_startDevBox operation. */
export interface DevBoxesStartDevBoxExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_stopDevBox operation. */
export interface DevBoxesStopDevBoxHeaders {
  /** URL to query for status of the operation. */
  operationLocation?: string;
}

/** Defines headers for DevBoxes_stopDevBox operation. */
export interface DevBoxesStopDevBoxExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_restartDevBox operation. */
export interface DevBoxesRestartDevBoxHeaders {
  /** URL to query for status of the operation. */
  operationLocation?: string;
}

/** Defines headers for DevBoxes_restartDevBox operation. */
export interface DevBoxesRestartDevBoxExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_repairDevBox operation. */
export interface DevBoxesRepairDevBoxHeaders {
  /** URL to query for status of the operation. */
  operationLocation?: string;
}

/** Defines headers for DevBoxes_repairDevBox operation. */
export interface DevBoxesRepairDevBoxExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_listCustomizationGroups operation. */
export interface DevBoxesListCustomizationGroupsExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_getCustomizationGroup operation. */
export interface DevBoxesGetCustomizationGroupExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_createCustomizationGroup operation. */
export interface DevBoxesCreateCustomizationGroupExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_getCustomizationTaskLog operation. */
export interface DevBoxesGetCustomizationTaskLogExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_getRemoteConnection operation. */
export interface DevBoxesGetRemoteConnectionExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_listActions operation. */
export interface DevBoxesListActionsExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_getAction operation. */
export interface DevBoxesGetActionExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_skipAction operation. */
export interface DevBoxesSkipActionExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_delayAction operation. */
export interface DevBoxesDelayActionExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_delayActions operation. */
export interface DevBoxesDelayActionsExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_listOperations operation. */
export interface DevBoxesListOperationsExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_getOperation operation. */
export interface DevBoxesGetOperationExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_listPoolsNext operation. */
export interface DevBoxesListPoolsNextExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_listSchedulesByProjectNext operation. */
export interface DevBoxesListSchedulesByProjectNextExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_listSchedulesByPoolNext operation. */
export interface DevBoxesListSchedulesByPoolNextExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_listDevBoxesByUserNext operation. */
export interface DevBoxesListDevBoxesByUserNextExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_listCustomizationGroupsNext operation. */
export interface DevBoxesListCustomizationGroupsNextExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_listActionsNext operation. */
export interface DevBoxesListActionsNextExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_delayActionsNext operation. */
export interface DevBoxesDelayActionsNextExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for DevBoxes_listOperationsNext operation. */
export interface DevBoxesListOperationsNextExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Projects_validateCustomizationTasks operation. */
export interface ProjectsValidateCustomizationTasksHeaders {
  /** URL to query for status of the operation. */
  operationLocation?: string;
}

/** Defines headers for Environments_listEnvironments operation. */
export interface EnvironmentsListEnvironmentsExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_listEnvironmentsByUser operation. */
export interface EnvironmentsListEnvironmentsByUserExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_getEnvironmentByUser operation. */
export interface EnvironmentsGetEnvironmentByUserExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_createOrReplaceEnvironment operation. */
export interface EnvironmentsCreateOrReplaceEnvironmentHeaders {
  /** URL to query for status of the operation. */
  operationLocation?: string;
}

/** Defines headers for Environments_createOrReplaceEnvironment operation. */
export interface EnvironmentsCreateOrReplaceEnvironmentExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_patchEnvironment operation. */
export interface EnvironmentsPatchEnvironmentExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_deleteEnvironment operation. */
export interface EnvironmentsDeleteEnvironmentHeaders {
  /** URL to query for status of the operation. */
  operationLocation?: string;
}

/** Defines headers for Environments_deleteEnvironment operation. */
export interface EnvironmentsDeleteEnvironmentExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_getOutputs operation. */
export interface EnvironmentsGetOutputsExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_listOperations operation. */
export interface EnvironmentsListOperationsExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_getOperation operation. */
export interface EnvironmentsGetOperationExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_getLogsByOperation operation. */
export interface EnvironmentsGetLogsByOperationExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_listActions operation. */
export interface EnvironmentsListActionsExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_getAction operation. */
export interface EnvironmentsGetActionExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_skipAction operation. */
export interface EnvironmentsSkipActionExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_delayAction operation. */
export interface EnvironmentsDelayActionExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_listCatalogsByProject operation. */
export interface EnvironmentsListCatalogsByProjectExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_getCatalog operation. */
export interface EnvironmentsGetCatalogExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_listEnvironmentDefinitionsByProject operation. */
export interface EnvironmentsListEnvironmentDefinitionsByProjectExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_listEnvironmentDefinitionsByCatalog operation. */
export interface EnvironmentsListEnvironmentDefinitionsByCatalogExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_getEnvironmentDefinition operation. */
export interface EnvironmentsGetEnvironmentDefinitionExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_listEnvironmentTypes operation. */
export interface EnvironmentsListEnvironmentTypesExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_getEnvironmentType operation. */
export interface EnvironmentsGetEnvironmentTypeExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_getEnvironmentTypeAbilities operation. */
export interface EnvironmentsGetEnvironmentTypeAbilitiesExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_listArtifactsByEnvironment operation. */
export interface EnvironmentsListArtifactsByEnvironmentExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_listArtifactsByEnvironmentAndPath operation. */
export interface EnvironmentsListArtifactsByEnvironmentAndPathExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_listEnvironmentsNext operation. */
export interface EnvironmentsListEnvironmentsNextExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_listEnvironmentsByUserNext operation. */
export interface EnvironmentsListEnvironmentsByUserNextExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_listOperationsNext operation. */
export interface EnvironmentsListOperationsNextExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_listActionsNext operation. */
export interface EnvironmentsListActionsNextExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_listCatalogsByProjectNext operation. */
export interface EnvironmentsListCatalogsByProjectNextExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_listEnvironmentDefinitionsByProjectNext operation. */
export interface EnvironmentsListEnvironmentDefinitionsByProjectNextExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_listEnvironmentDefinitionsByCatalogNext operation. */
export interface EnvironmentsListEnvironmentDefinitionsByCatalogNextExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_listEnvironmentTypesNext operation. */
export interface EnvironmentsListEnvironmentTypesNextExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_listArtifactsByEnvironmentNext operation. */
export interface EnvironmentsListArtifactsByEnvironmentNextExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Defines headers for Environments_listArtifactsByEnvironmentAndPathNext operation. */
export interface EnvironmentsListArtifactsByEnvironmentAndPathNextExceptionHeaders {
  /** The error code for specific error that occurred. */
  xMsErrorCode?: string;
}

/** Known values of {@link CustomizationTaskExecutionAccount} that the service accepts. */
export enum KnownCustomizationTaskExecutionAccount {
  /** The SYSTEM account. */
  System = "System",
  /** The account of the signed in user. */
  User = "User",
}

/**
 * Defines values for CustomizationTaskExecutionAccount. \
 * {@link KnownCustomizationTaskExecutionAccount} can be used interchangeably with CustomizationTaskExecutionAccount,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **System**: The SYSTEM account. \
 * **User**: The account of the signed in user.
 */
export type CustomizationTaskExecutionAccount = string;
/** Defines values for ProjectAbilityAsAdmin. */
export type ProjectAbilityAsAdmin =
  | "DeleteDevBoxes"
  | "DeleteEnvironments"
  | "ManageEnvironmentActions"
  | "ReadDevBoxes"
  | "ReadEnvironmentActions"
  | "ReadEnvironmentOutputs"
  | "ReadEnvironments"
  | "StartDevBoxes"
  | "StopDevBoxes"
  | "WriteDevBoxes"
  | "WriteEnvironments";
/** Defines values for ProjectAbilityAsDeveloper. */
export type ProjectAbilityAsDeveloper =
  | "CustomizeDevBoxes"
  | "DeleteDevBoxes"
  | "DeleteEnvironments"
  | "ManageDevBoxActions"
  | "ManageEnvironmentActions"
  | "ReadDevBoxActions"
  | "ReadDevBoxes"
  | "ReadEnvironmentActions"
  | "ReadEnvironmentOutputs"
  | "ReadEnvironments"
  | "ReadRemoteConnections"
  | "StartDevBoxes"
  | "StopDevBoxes"
  | "WriteDevBoxes"
  | "WriteEnvironments";
/** Defines values for HibernateSupport. */
export type HibernateSupport = "Enabled" | "Disabled" | "OsUnsupported";
/** Defines values for LocalAdminStatus. */
export type LocalAdminStatus = "Enabled" | "Disabled";
/** Defines values for StopOnDisconnectEnableStatus. */
export type StopOnDisconnectEnableStatus = "Enabled" | "Disabled";
/** Defines values for PoolHealthStatus. */
export type PoolHealthStatus =
  | "Unknown"
  | "Pending"
  | "Healthy"
  | "Warning"
  | "Unhealthy";
/** Defines values for CustomizationTaskDefinitionParameterType. */
export type CustomizationTaskDefinitionParameterType =
  | "string"
  | "number"
  | "boolean";
/** Defines values for CustomizationTaskStatus. */
export type CustomizationTaskStatus =
  | "NotStarted"
  | "Running"
  | "Succeeded"
  | "FailedValidation"
  | "Skipped"
  | "TimedOut"
  | "Failed"
  | "WaitingForUserInputUac"
  | "WaitingForUserSession";
/** Defines values for CustomizationTaskListValidationStatus. */
export type CustomizationTaskListValidationStatus = "Succeeded" | "Failed";
/** Defines values for PowerState. */
export type PowerState =
  | "Unknown"
  | "Running"
  | "Deallocated"
  | "PoweredOff"
  | "Hibernated";
/** Defines values for CustomizationGroupStatus. */
export type CustomizationGroupStatus =
  | "NotStarted"
  | "Running"
  | "Succeeded"
  | "Failed"
  | "ValidationFailed";
/** Defines values for DevBoxActionSourceType. */
export type DevBoxActionSourceType = "Pool" | "Schedule";
/** Defines values for DevBoxActionDelayResultStatus. */
export type DevBoxActionDelayResultStatus = "Succeeded" | "Failed";
/** Defines values for DevBoxOperationKind. */
export type DevBoxOperationKind = "Start" | "Stop" | "Restart" | "Repair";
/** Defines values for DevBoxOperationStatus. */
export type DevBoxOperationStatus =
  | "NotStarted"
  | "Running"
  | "Succeeded"
  | "Canceled"
  | "Failed";
/** Defines values for EnvironmentOutputType. */
export type EnvironmentOutputType =
  | "array"
  | "boolean"
  | "number"
  | "object"
  | "string";
/** Defines values for EnvironmentOperationKind. */
export type EnvironmentOperationKind = "Deploy" | "Delete";
/** Defines values for EnvironmentOperationStatus. */
export type EnvironmentOperationStatus =
  | "NotStarted"
  | "Running"
  | "Succeeded"
  | "Canceled"
  | "Failed";
/** Defines values for ParameterType. */
export type ParameterType =
  | "array"
  | "boolean"
  | "integer"
  | "number"
  | "object"
  | "string";
/** Defines values for EnvironmentTypeEnableStatus. */
export type EnvironmentTypeEnableStatus = "Enabled" | "Disabled";
/** Defines values for EnvironmentTypeAbilityAsAdmin. */
export type EnvironmentTypeAbilityAsAdmin =
  | "DeleteEnvironments"
  | "ManageEnvironmentActions"
  | "ReadEnvironmentActions"
  | "ReadEnvironmentOutputs"
  | "ReadEnvironments"
  | "WriteEnvironments";
/** Defines values for EnvironmentTypeAbilityAsDeveloper. */
export type EnvironmentTypeAbilityAsDeveloper =
  | "DeleteEnvironments"
  | "ManageEnvironmentActions"
  | "ReadEnvironmentActions"
  | "ReadEnvironmentOutputs"
  | "ReadEnvironments"
  | "WriteEnvironments";
/** Defines values for DevBoxRepairOutcome. */
export type DevBoxRepairOutcome =
  | "FixApplied"
  | "IssuesDetected"
  | "NoIssuesDetected";

/** Optional parameters. */
export interface DevCenterListProjectsOptionalParams
  extends coreClient.OperationOptions {
  /** An OData filter clause to apply to the operation. */
  filter?: string;
  /** The maximum number of resources to return from the operation. Example: 'top=10'. */
  top?: number;
}

/** Contains response data for the listProjects operation. */
export type DevCenterListProjectsResponse = ProjectListResult;

/** Optional parameters. */
export interface DevCenterGetProjectOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getProject operation. */
export type DevCenterGetProjectResponse = Project;

/** Optional parameters. */
export interface DevCenterGetProjectAbilitiesOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getProjectAbilities operation. */
export type DevCenterGetProjectAbilitiesResponse = ProjectAbilities;

/** Optional parameters. */
export interface DevCenterListAllDevBoxesOptionalParams
  extends coreClient.OperationOptions {
  /** An OData filter clause to apply to the operation. */
  filter?: string;
  /** The maximum number of resources to return from the operation. Example: 'top=10'. */
  top?: number;
}

/** Contains response data for the listAllDevBoxes operation. */
export type DevCenterListAllDevBoxesResponse = DevBoxListResult;

/** Optional parameters. */
export interface DevCenterListAllDevBoxesByUserOptionalParams
  extends coreClient.OperationOptions {
  /** An OData filter clause to apply to the operation. */
  filter?: string;
  /** The maximum number of resources to return from the operation. Example: 'top=10'. */
  top?: number;
}

/** Contains response data for the listAllDevBoxesByUser operation. */
export type DevCenterListAllDevBoxesByUserResponse = DevBoxListResult;

/** Optional parameters. */
export interface DevCenterGetNotificationSettingsOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getNotificationSettings operation. */
export type DevCenterGetNotificationSettingsResponse = NotificationSettings;

/** Optional parameters. */
export interface DevCenterCreateNotificationSettingsOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the createNotificationSettings operation. */
export type DevCenterCreateNotificationSettingsResponse = NotificationSettings;

/** Optional parameters. */
export interface DevCenterListNotificationSettingsAllowedCulturesOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listNotificationSettingsAllowedCultures operation. */
export type DevCenterListNotificationSettingsAllowedCulturesResponse =
  NotificationSettingsAllowedCulturesListResult;

/** Optional parameters. */
export interface DevCenterListProjectsNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listProjectsNext operation. */
export type DevCenterListProjectsNextResponse = ProjectListResult;

/** Optional parameters. */
export interface DevCenterListAllDevBoxesNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listAllDevBoxesNext operation. */
export type DevCenterListAllDevBoxesNextResponse = DevBoxListResult;

/** Optional parameters. */
export interface DevCenterListAllDevBoxesByUserNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listAllDevBoxesByUserNext operation. */
export type DevCenterListAllDevBoxesByUserNextResponse = DevBoxListResult;

/** Optional parameters. */
export interface DevBoxesListPoolsOptionalParams
  extends coreClient.OperationOptions {
  /** An OData filter clause to apply to the operation. */
  filter?: string;
  /** The maximum number of resources to return from the operation. Example: 'top=10'. */
  top?: number;
}

/** Contains response data for the listPools operation. */
export type DevBoxesListPoolsResponse = PoolListResult;

/** Optional parameters. */
export interface DevBoxesGetPoolOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getPool operation. */
export type DevBoxesGetPoolResponse = Pool;

/** Optional parameters. */
export interface DevBoxesListSchedulesByProjectOptionalParams
  extends coreClient.OperationOptions {
  /** An OData filter clause to apply to the operation. */
  filter?: string;
  /** The maximum number of resources to return from the operation. Example: 'top=10'. */
  top?: number;
}

/** Contains response data for the listSchedulesByProject operation. */
export type DevBoxesListSchedulesByProjectResponse = ScheduleListResult;

/** Optional parameters. */
export interface DevBoxesListSchedulesByPoolOptionalParams
  extends coreClient.OperationOptions {
  /** An OData filter clause to apply to the operation. */
  filter?: string;
  /** The maximum number of resources to return from the operation. Example: 'top=10'. */
  top?: number;
}

/** Contains response data for the listSchedulesByPool operation. */
export type DevBoxesListSchedulesByPoolResponse = ScheduleListResult;

/** Optional parameters. */
export interface DevBoxesGetScheduleByPoolOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getScheduleByPool operation. */
export type DevBoxesGetScheduleByPoolResponse = Schedule;

/** Optional parameters. */
export interface DevBoxesListDevBoxesByUserOptionalParams
  extends coreClient.OperationOptions {
  /** An OData filter clause to apply to the operation. */
  filter?: string;
  /** The maximum number of resources to return from the operation. Example: 'top=10'. */
  top?: number;
}

/** Contains response data for the listDevBoxesByUser operation. */
export type DevBoxesListDevBoxesByUserResponse = DevBoxListResult;

/** Optional parameters. */
export interface DevBoxesGetDevBoxByUserOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getDevBoxByUser operation. */
export type DevBoxesGetDevBoxByUserResponse = DevBox;

/** Optional parameters. */
export interface DevBoxesCreateDevBoxOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the createDevBox operation. */
export type DevBoxesCreateDevBoxResponse = DevBox;

/** Optional parameters. */
export interface DevBoxesDeleteDevBoxOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the deleteDevBox operation. */
export type DevBoxesDeleteDevBoxResponse = DevBoxesDeleteDevBoxHeaders &
  OperationStatus;

/** Optional parameters. */
export interface DevBoxesStartDevBoxOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the startDevBox operation. */
export type DevBoxesStartDevBoxResponse = DevBoxesStartDevBoxHeaders &
  OperationStatus;

/** Optional parameters. */
export interface DevBoxesStopDevBoxOptionalParams
  extends coreClient.OperationOptions {
  /** Optional parameter to hibernate the dev box. */
  hibernate?: boolean;
}

/** Contains response data for the stopDevBox operation. */
export type DevBoxesStopDevBoxResponse = DevBoxesStopDevBoxHeaders &
  OperationStatus;

/** Optional parameters. */
export interface DevBoxesRestartDevBoxOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the restartDevBox operation. */
export type DevBoxesRestartDevBoxResponse = DevBoxesRestartDevBoxHeaders &
  OperationStatus;

/** Optional parameters. */
export interface DevBoxesRepairDevBoxOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the repairDevBox operation. */
export type DevBoxesRepairDevBoxResponse = DevBoxesRepairDevBoxHeaders &
  OperationStatus;

/** Optional parameters. */
export interface DevBoxesListCustomizationGroupsOptionalParams
  extends coreClient.OperationOptions {
  /** Optional query parameter to specify what properties should be included in the response. */
  include?: string[];
}

/** Contains response data for the listCustomizationGroups operation. */
export type DevBoxesListCustomizationGroupsResponse =
  CustomizationGroupListResult;

/** Optional parameters. */
export interface DevBoxesGetCustomizationGroupOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getCustomizationGroup operation. */
export type DevBoxesGetCustomizationGroupResponse = CustomizationGroup;

/** Optional parameters. */
export interface DevBoxesCreateCustomizationGroupOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the createCustomizationGroup operation. */
export type DevBoxesCreateCustomizationGroupResponse = CustomizationGroup;

/** Optional parameters. */
export interface DevBoxesGetCustomizationTaskLogOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getCustomizationTaskLog operation. */
export type DevBoxesGetCustomizationTaskLogResponse = {
  /** The parsed response body. */
  body: coreRestPipeline.RequestBodyType;
};

/** Optional parameters. */
export interface DevBoxesGetRemoteConnectionOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getRemoteConnection operation. */
export type DevBoxesGetRemoteConnectionResponse = RemoteConnection;

/** Optional parameters. */
export interface DevBoxesListActionsOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listActions operation. */
export type DevBoxesListActionsResponse = DevBoxActionsListResult;

/** Optional parameters. */
export interface DevBoxesGetActionOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getAction operation. */
export type DevBoxesGetActionResponse = DevBoxAction;

/** Optional parameters. */
export interface DevBoxesSkipActionOptionalParams
  extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface DevBoxesDelayActionOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the delayAction operation. */
export type DevBoxesDelayActionResponse = DevBoxAction;

/** Optional parameters. */
export interface DevBoxesDelayActionsOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the delayActions operation. */
export type DevBoxesDelayActionsResponse = DevBoxActionsDelayMultipleResult;

/** Optional parameters. */
export interface DevBoxesListOperationsOptionalParams
  extends coreClient.OperationOptions {
  /** An OData filter clause to apply to the operation. */
  filter?: string;
  /** The maximum number of resources to return from the operation. Example: 'top=10'. */
  top?: number;
}

/** Contains response data for the listOperations operation. */
export type DevBoxesListOperationsResponse = DevBoxOperationListResult;

/** Optional parameters. */
export interface DevBoxesGetOperationOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getOperation operation. */
export type DevBoxesGetOperationResponse = DevBoxOperationUnion;

/** Optional parameters. */
export interface DevBoxesListPoolsNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listPoolsNext operation. */
export type DevBoxesListPoolsNextResponse = PoolListResult;

/** Optional parameters. */
export interface DevBoxesListSchedulesByProjectNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listSchedulesByProjectNext operation. */
export type DevBoxesListSchedulesByProjectNextResponse = ScheduleListResult;

/** Optional parameters. */
export interface DevBoxesListSchedulesByPoolNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listSchedulesByPoolNext operation. */
export type DevBoxesListSchedulesByPoolNextResponse = ScheduleListResult;

/** Optional parameters. */
export interface DevBoxesListDevBoxesByUserNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listDevBoxesByUserNext operation. */
export type DevBoxesListDevBoxesByUserNextResponse = DevBoxListResult;

/** Optional parameters. */
export interface DevBoxesListCustomizationGroupsNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listCustomizationGroupsNext operation. */
export type DevBoxesListCustomizationGroupsNextResponse =
  CustomizationGroupListResult;

/** Optional parameters. */
export interface DevBoxesListActionsNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listActionsNext operation. */
export type DevBoxesListActionsNextResponse = DevBoxActionsListResult;

/** Optional parameters. */
export interface DevBoxesDelayActionsNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the delayActionsNext operation. */
export type DevBoxesDelayActionsNextResponse = DevBoxActionsDelayMultipleResult;

/** Optional parameters. */
export interface DevBoxesListOperationsNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listOperationsNext operation. */
export type DevBoxesListOperationsNextResponse = DevBoxOperationListResult;

/** Optional parameters. */
export interface ProjectsListCustomizationTaskDefinitionsOptionalParams
  extends coreClient.OperationOptions {
  /** An OData filter clause to apply to the operation. */
  filter?: string;
}

/** Contains response data for the listCustomizationTaskDefinitions operation. */
export type ProjectsListCustomizationTaskDefinitionsResponse =
  CustomizationTaskDefinitionListResult;

/** Optional parameters. */
export interface ProjectsGetCustomizationTaskDefinitionOptionalParams
  extends coreClient.OperationOptions {
  /** An OData filter clause to apply to the operation. */
  filter?: string;
  /** The maximum number of resources to return from the operation. Example: 'top=10'. */
  top?: number;
}

/** Contains response data for the getCustomizationTaskDefinition operation. */
export type ProjectsGetCustomizationTaskDefinitionResponse =
  CustomizationTaskDefinition;

/** Optional parameters. */
export interface ProjectsValidateCustomizationTasksOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the validateCustomizationTasks operation. */
export type ProjectsValidateCustomizationTasksResponse =
  ProjectsValidateCustomizationTasksHeaders &
    CustomizationTaskListValidationOperationResult;

/** Optional parameters. */
export interface ProjectsListCustomizationTaskDefinitionsNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listCustomizationTaskDefinitionsNext operation. */
export type ProjectsListCustomizationTaskDefinitionsNextResponse =
  CustomizationTaskDefinitionListResult;

/** Optional parameters. */
export interface EnvironmentsListEnvironmentsOptionalParams
  extends coreClient.OperationOptions {
  /** The maximum number of resources to return from the operation. Example: 'top=10'. */
  top?: number;
}

/** Contains response data for the listEnvironments operation. */
export type EnvironmentsListEnvironmentsResponse = EnvironmentListResult;

/** Optional parameters. */
export interface EnvironmentsListEnvironmentsByUserOptionalParams
  extends coreClient.OperationOptions {
  /** The maximum number of resources to return from the operation. Example: 'top=10'. */
  top?: number;
}

/** Contains response data for the listEnvironmentsByUser operation. */
export type EnvironmentsListEnvironmentsByUserResponse = EnvironmentListResult;

/** Optional parameters. */
export interface EnvironmentsGetEnvironmentByUserOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getEnvironmentByUser operation. */
export type EnvironmentsGetEnvironmentByUserResponse = Environment;

/** Optional parameters. */
export interface EnvironmentsCreateOrReplaceEnvironmentOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the createOrReplaceEnvironment operation. */
export type EnvironmentsCreateOrReplaceEnvironmentResponse =
  EnvironmentsCreateOrReplaceEnvironmentHeaders & Environment;

/** Optional parameters. */
export interface EnvironmentsPatchEnvironmentOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the patchEnvironment operation. */
export type EnvironmentsPatchEnvironmentResponse = Environment;

/** Optional parameters. */
export interface EnvironmentsDeleteEnvironmentOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the deleteEnvironment operation. */
export type EnvironmentsDeleteEnvironmentResponse =
  EnvironmentsDeleteEnvironmentHeaders & OperationStatus;

/** Optional parameters. */
export interface EnvironmentsGetOutputsOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getOutputs operation. */
export type EnvironmentsGetOutputsResponse = EnvironmentOutputs;

/** Optional parameters. */
export interface EnvironmentsListOperationsOptionalParams
  extends coreClient.OperationOptions {
  /** An OData filter clause to apply to the operation. */
  filter?: string;
  /** The maximum number of resources to return from the operation. Example: 'top=10'. */
  top?: number;
}

/** Contains response data for the listOperations operation. */
export type EnvironmentsListOperationsResponse = EnvironmentOperationListResult;

/** Optional parameters. */
export interface EnvironmentsGetOperationOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getOperation operation. */
export type EnvironmentsGetOperationResponse = EnvironmentOperationUnion;

/** Optional parameters. */
export interface EnvironmentsGetLogsByOperationOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getLogsByOperation operation. */
export type EnvironmentsGetLogsByOperationResponse = {
  /** The parsed response body. */
  body: coreRestPipeline.RequestBodyType;
};

/** Optional parameters. */
export interface EnvironmentsListActionsOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listActions operation. */
export type EnvironmentsListActionsResponse = EnvironmentActionsListResult;

/** Optional parameters. */
export interface EnvironmentsGetActionOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getAction operation. */
export type EnvironmentsGetActionResponse = EnvironmentAction;

/** Optional parameters. */
export interface EnvironmentsSkipActionOptionalParams
  extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface EnvironmentsDelayActionOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the delayAction operation. */
export type EnvironmentsDelayActionResponse = EnvironmentAction;

/** Optional parameters. */
export interface EnvironmentsListCatalogsByProjectOptionalParams
  extends coreClient.OperationOptions {
  /** The maximum number of resources to return from the operation. Example: 'top=10'. */
  top?: number;
}

/** Contains response data for the listCatalogsByProject operation. */
export type EnvironmentsListCatalogsByProjectResponse = CatalogListResult;

/** Optional parameters. */
export interface EnvironmentsGetCatalogOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getCatalog operation. */
export type EnvironmentsGetCatalogResponse = Catalog;

/** Optional parameters. */
export interface EnvironmentsListEnvironmentDefinitionsByProjectOptionalParams
  extends coreClient.OperationOptions {
  /** The maximum number of resources to return from the operation. Example: 'top=10'. */
  top?: number;
}

/** Contains response data for the listEnvironmentDefinitionsByProject operation. */
export type EnvironmentsListEnvironmentDefinitionsByProjectResponse =
  EnvironmentDefinitionListResult;

/** Optional parameters. */
export interface EnvironmentsListEnvironmentDefinitionsByCatalogOptionalParams
  extends coreClient.OperationOptions {
  /** The maximum number of resources to return from the operation. Example: 'top=10'. */
  top?: number;
}

/** Contains response data for the listEnvironmentDefinitionsByCatalog operation. */
export type EnvironmentsListEnvironmentDefinitionsByCatalogResponse =
  EnvironmentDefinitionListResult;

/** Optional parameters. */
export interface EnvironmentsGetEnvironmentDefinitionOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getEnvironmentDefinition operation. */
export type EnvironmentsGetEnvironmentDefinitionResponse =
  EnvironmentDefinition;

/** Optional parameters. */
export interface EnvironmentsListEnvironmentTypesOptionalParams
  extends coreClient.OperationOptions {
  /** The maximum number of resources to return from the operation. Example: 'top=10'. */
  top?: number;
}

/** Contains response data for the listEnvironmentTypes operation. */
export type EnvironmentsListEnvironmentTypesResponse =
  EnvironmentTypeListResult;

/** Optional parameters. */
export interface EnvironmentsGetEnvironmentTypeOptionalParams
  extends coreClient.OperationOptions {
  /** The maximum number of resources to return from the operation. Example: 'top=10'. */
  top?: number;
}

/** Contains response data for the getEnvironmentType operation. */
export type EnvironmentsGetEnvironmentTypeResponse = EnvironmentType;

/** Optional parameters. */
export interface EnvironmentsGetEnvironmentTypeAbilitiesOptionalParams
  extends coreClient.OperationOptions {
  /** The maximum number of resources to return from the operation. Example: 'top=10'. */
  top?: number;
}

/** Contains response data for the getEnvironmentTypeAbilities operation. */
export type EnvironmentsGetEnvironmentTypeAbilitiesResponse =
  EnvironmentTypeAbilities;

/** Optional parameters. */
export interface EnvironmentsListArtifactsByEnvironmentOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listArtifactsByEnvironment operation. */
export type EnvironmentsListArtifactsByEnvironmentResponse = ArtifactListResult;

/** Optional parameters. */
export interface EnvironmentsListArtifactsByEnvironmentAndPathOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listArtifactsByEnvironmentAndPath operation. */
export type EnvironmentsListArtifactsByEnvironmentAndPathResponse =
  ArtifactListResult;

/** Optional parameters. */
export interface EnvironmentsListEnvironmentsNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listEnvironmentsNext operation. */
export type EnvironmentsListEnvironmentsNextResponse = EnvironmentListResult;

/** Optional parameters. */
export interface EnvironmentsListEnvironmentsByUserNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listEnvironmentsByUserNext operation. */
export type EnvironmentsListEnvironmentsByUserNextResponse =
  EnvironmentListResult;

/** Optional parameters. */
export interface EnvironmentsListOperationsNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listOperationsNext operation. */
export type EnvironmentsListOperationsNextResponse =
  EnvironmentOperationListResult;

/** Optional parameters. */
export interface EnvironmentsListActionsNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listActionsNext operation. */
export type EnvironmentsListActionsNextResponse = EnvironmentActionsListResult;

/** Optional parameters. */
export interface EnvironmentsListCatalogsByProjectNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listCatalogsByProjectNext operation. */
export type EnvironmentsListCatalogsByProjectNextResponse = CatalogListResult;

/** Optional parameters. */
export interface EnvironmentsListEnvironmentDefinitionsByProjectNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listEnvironmentDefinitionsByProjectNext operation. */
export type EnvironmentsListEnvironmentDefinitionsByProjectNextResponse =
  EnvironmentDefinitionListResult;

/** Optional parameters. */
export interface EnvironmentsListEnvironmentDefinitionsByCatalogNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listEnvironmentDefinitionsByCatalogNext operation. */
export type EnvironmentsListEnvironmentDefinitionsByCatalogNextResponse =
  EnvironmentDefinitionListResult;

/** Optional parameters. */
export interface EnvironmentsListEnvironmentTypesNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listEnvironmentTypesNext operation. */
export type EnvironmentsListEnvironmentTypesNextResponse =
  EnvironmentTypeListResult;

/** Optional parameters. */
export interface EnvironmentsListArtifactsByEnvironmentNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listArtifactsByEnvironmentNext operation. */
export type EnvironmentsListArtifactsByEnvironmentNextResponse =
  ArtifactListResult;

/** Optional parameters. */
export interface EnvironmentsListArtifactsByEnvironmentAndPathNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listArtifactsByEnvironmentAndPathNext operation. */
export type EnvironmentsListArtifactsByEnvironmentAndPathNextResponse =
  ArtifactListResult;

/** Optional parameters. */
export interface DevCenterClientOptionalParams
  extends coreClient.ServiceClientOptions {
  /** Api Version */
  apiVersion?: string;
  /** Overrides client endpoint. */
  endpoint?: string;
}
